import React, { useEffect, useRef, useState } from 'react'
import injectSheet from 'react-jss'
import { apiClient } from 'pobble-components/dist/api'
import { useParams } from 'react-router-dom'
import { Presentation } from 'pobble-components/dist/presentation'
import logo from './logo.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpandAlt } from '@fortawesome/pro-regular-svg-icons/faExpandAlt'
import ButtonElement from 'pobble-components/dist/button-element'
import { usePresentMode } from '../direct-present/present-mode'
import Button from 'pobble-components/dist/button'
import { AUTH_REDIRECT } from 'pobble-components/dist/cookies'
import { useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min'
import { URLS } from 'pobble-components/dist/setup'
import { getToken } from 'pobble-components/dist/auth-helpers'
import { useMe } from 'pobble-components/dist/pobble-user'

const CTA = injectSheet({
  cta: {
    background: '#242424',
    padding: '48px 24px',
    textAlign: 'center',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
    margin: '56px -24px',
    '@media (min-width: 960px)': {
      margin: '32px calc(-100vw / 2 + 960px / 2);'
    },
    '& *': {
      margin: '0'
    }
  },
  button: {
    marginTop: '16px'
  }
})(({ classes }) => {
  const token = getToken()

  const { params } = useRouteMatch()
  const { me } = useMe()

  return (
    <section className={classes.cta}>
      <h2>Are you a teacher? Inspire your young writers!</h2>
      <p>
        Find a new exciting writing prompt like this, free, every day on Pobble
      </p>
      <p>
        Save your copy, edit activities, share on Google Classroom and more!
      </p>
      {!token && (
        <Button
          onClick={() => {
            // Set sign up redirect
            AUTH_REDIRECT.set(`${URLS.APP}/lessons/prompt/${params.id}`)
          }}
          className={`${classes.button} metrics__public-preview-cta--anonymous`}
          href="/auth/sign-up?from=public-preview"
          _blank
          tall
          minw
          darkBlue>
          Sign up for free
        </Button>
      )}
      {token && (
        <Button
          onClick={() => {
            // Set sign in redirect only if user is not logged in
            if (!me) {
              AUTH_REDIRECT.set(`${URLS.APP}/lessons/prompt/${params.id}`)
            }
          }}
          className={`${classes.button} metrics__public-preview-cta--existing`}
          href={`${URLS.APP}/lessons/prompt/${params.id}`}
          _blank
          tall
          darkBlue>
          Go to Pobble
        </Button>
      )}
    </section>
  )
})

export const PublicPreview = injectSheet({
  container: {
    minHeight: '100vh',
    background: '#191919',
    overflow: 'hidden',
    padding: '24px'
  },
  skeleton: {
    maxWidth: '960px',
    margin: '0 auto'
  },
  bone: {
    paddingBottom: '56%',
    background: '#1f1f1f',
    borderRadius: '8px',
    marginTop: '24px',
    '&:first-child': {
      marginTop: '0'
    }
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'var(--light-gray)',
    gap: '8px',
    marginBottom: '16px',
    '& p': {
      margin: '0',
      transform: 'translate(0, -8px)',
      opacity: '0',
      animation: '.8s appear .4s ease-out forwards'
    }
  },
  slidesContainer: {
    maxWidth: '960px',
    margin: '0 auto'
  },
  slide: {
    borderRadius: '8px',
    transform: 'translateZ(0)',
    background: '#1f1f1f',
    overflow: 'hidden',
    marginTop: '24px',
    position: 'relative',
    '&:first-child': {
      marginTop: '0'
    }
  },
  slideHolder: {
    opacity: '0',
    animation: 'appear .3s ease-out forwards'
  },
  logo: {
    display: 'block',
    width: '80px',
    margin: '0 auto',
    height: '46px',
    transform: 'translate(0, -8px)',
    opacity: '0',
    animation: 'appear .8s ease-out forwards',
    '& img': {
      width: '100%'
    }
  },
  fullScreenButton: {
    fontSize: '28px',
    position: 'absolute',
    right: '0',
    top: '0',
    padding: '16px',
    color: 'rgba(255, 255, 255, .7)',
    '& svg': {
      stroke: '#000',
      strokeWidth: '4px',
      strokeOpacity: '.5'
    }
  },
  '@keyframes appear': {
    to: {
      opacity: '1',
      transform: 'translate(0, 0)'
    }
  }
})(({ classes }) => {
  const [presentation, setPresentation] = useState(null)
  const { id } = useParams()
  const slidesContainerRef = useRef(null)
  const [presentationReady, setReady] = useState(false)
  const { present } = usePresentMode()

  useEffect(
    () => {
      if (presentation) {
        document.title = `${presentation.title} - Pobble`
      }
    },
    [presentation]
  )

  /**
   * Preload the first image of the presentation
   *  so that users don't see progressive image laoding
   */
  useEffect(
    () => {
      let timer = null
      let ready = false

      const setAsReady = () => {
        if (!ready) {
          setReady(true)
          ready = true
        }
      }

      if (presentation) {
        const image =
          !presentation.asset && presentation.image && presentation.image.url

        if (!image) {
          setAsReady()
        } else {
          const img = new Image()
          img.src = image
          img.onload = () => setAsReady()

          /**
           * 3 second fallback in cases when image doesn't load
           */
          timer = setTimeout(() => setAsReady(), 3000)
        }
      }

      return () => {
        clearTimeout(timer)
      }
    },
    [presentation]
  )

  useEffect(
    () => {
      setPresentation(null)
      apiClient
        .get(`/v3/presentations/${id}`, {
          params: {
            include:
              'presentation.p365,presentation.account,presentation.category,presentation.status,presentation.image,presentation.year_groups,presentation.year_group.order,presentation.details,presentation.slides,presentation.slide.type,presentation.slide.details,presentation.names'
          }
        })
        .then(res => setPresentation(res.data.presentation))
    },
    [id]
  )

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <a
          className={classes.logo}
          target="_blank"
          rel="noopener noreferrer"
          href="https://pobble.com">
          <img src={logo} alt="Pobble" />
        </a>
        <p>Inspiring young writers!</p>
      </header>
      {!presentationReady && (
        <div className={classes.skeleton}>
          <div className={classes.bone} />
          <div className={classes.bone} />
          <div className={classes.bone} />
          <div className={classes.bone} />
        </div>
      )}
      {presentationReady && (
        <div ref={slidesContainerRef} className={classes.slidesContainer}>
          <Presentation
            containerRef={slidesContainerRef}
            multiSlides
            data={presentation}>
            {({ slides }) => {
              return slides.map((slide, i) => {
                return (
                  <>
                    <div className={classes.slide}>
                      <div className={classes.slideHolder}>
                        {slide}
                        {i === 0 && (
                          <>
                            <ButtonElement
                              onClick={() => present(presentation.id)}
                              className={classes.fullScreenButton}>
                              <FontAwesomeIcon icon={faExpandAlt} />
                            </ButtonElement>
                          </>
                        )}
                      </div>
                    </div>
                    {i === 3 && <CTA />}
                  </>
                )
              })
            }}
          </Presentation>
        </div>
      )}
    </div>
  )
})
